import { Info, Undo2, Users, X } from 'lucide-react';
import React, { useCallback, useContext, useState } from 'react';
import { TenantSelectionHelpPanel } from './TenantSelectionHelpPanel';
import { TenantSelector } from '../../../../components/tenant-selector/TenantSelector';
import type {
  TenantSelectionConflicts,
  TenantSelectorRowData,
} from '../../../../components/tenant-selector/types';
import { DialogBackdrop } from '../../../../components/ui/dialog-backdrop';
import type { TenantSelectionType } from '../../engage-automations-tab/templates.types';
import { AutomationEditorContext } from '../AutomationEditorContext';
import styles from './TenantSelectionControl.module.css';

export function TenantSelectionValidationStatus({
  conflicts,
  isValidating,
}: {
  conflicts: TenantSelectionConflicts;
  isValidating: boolean;
}) {
  if (isValidating) {
    return <p className={styles.validatingMessage}>Validating...</p>;
  }
  return (
    <div className={styles.segmentConflictWarning} role="alert">
      <p className={styles.segmentConflictTitle}>{conflicts.notice.title}</p>
      <p className={styles.segmentConflictDescription}>{conflicts.notice.description}</p>
      <ul className={styles.segmentConflictList}>
        {conflicts.items.map(conflict => (
          <li key={conflict.tenantId}>
            <strong>{conflict.tenantName}</strong> {conflict.description}
          </li>
        ))}
      </ul>
    </div>
  );
}

interface TenantSelectionControlProps {
  disabled?: boolean;
  tenantRowData: Record<string, TenantSelectorRowData>;
  conflicts: TenantSelectionConflicts | null;
  isValidating: boolean;
  isLoading?: boolean;
}

export const TenantSelectionControl: React.FC<TenantSelectionControlProps> = ({
  disabled = false,
  tenantRowData,
  conflicts,
  isValidating,
  isLoading,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { editor } = useContext(AutomationEditorContext)!;
  const { state, undoTenantSelection, updateTenantSelection } = editor;
  const isDirty = state.dirtySteps.tenantSelection;
  const { tenantSelection } = state;

  const [localSelectionType, setLocalSelectionType] = useState<TenantSelectionType>('all');
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>([]);

  const [helpOpen, setHelpOpen] = useState(false);

  const openDialog = useCallback(() => {
    setLocalSelectionType(tenantSelection.selectionType);
    setLocalSelectedIds(tenantSelection.selectedTenantIds);
    setHelpOpen(false);
    setDialogOpen(true);
  }, [tenantSelection]);

  const closeDialog = () => setDialogOpen(false);

  const handleConfirm = () => {
    updateTenantSelection(localSelectionType, localSelectedIds);
    setDialogOpen(false);
  };

  const handleLocalChange = (type: TenantSelectionType, ids: string[]) => {
    setLocalSelectionType(type);
    setLocalSelectedIds(ids);
  };

  return (
    <>
      {isDirty && (
        <button
          type="button"
          onClick={undoTenantSelection}
          className={styles.undoButton}
          aria-label="Undo tenant selection changes"
          title="Undo tenant selection changes"
          disabled={disabled}>
          <Undo2 />
        </button>
      )}
      <button
        type="button"
        onClick={openDialog}
        className={`${styles.tenantButton} ${isDirty ? styles.tenantButtonDirty : ''}`}
        disabled={disabled}
        title={disabled ? 'Not available while sync is in progress' : 'Tenant selection'}
        aria-label="Tenant selection">
        <Users className={styles.tenantIcon} />
        {isDirty && <span className={styles.dirtyDot} />}
      </button>

      {dialogOpen && (
        <>
          <DialogBackdrop onClose={closeDialog} />
          <dialog className={styles.dialog} open>
            <header>
              <hgroup>
                <h2>Tenant Selection</h2>
                <p>Choose which tenants receive this automation when you save.</p>
              </hgroup>
              <button
                type="button"
                onClick={() => setHelpOpen(o => !o)}
                className={styles.helpButton}
                aria-expanded={helpOpen}>
                <Info aria-hidden="true" />
                {helpOpen ? 'Hide explanation' : 'Learn more'}
              </button>
              <button type="button" onClick={closeDialog} className={styles.closeButton}>
                <X />
              </button>
            </header>

            <main>
              {helpOpen && <TenantSelectionHelpPanel context="automation" />}
              <TenantSelector
                tenantSelectionType={localSelectionType}
                selectedTenantIds={localSelectedIds}
                onChange={handleLocalChange}
                allTenantsSelectionDescription="New tenants will automatically receive this automation when they're added."
                selectedTenantsSelectionDescription="Only the tenants you select below will receive this automation. New tenants will not receive it unless you add them."
                disabled={disabled}
                tenantRowData={tenantRowData}
                isLoading={isLoading}
              />

              {localSelectionType === 'selected' && localSelectedIds.length > 0 && conflicts && (
                <TenantSelectionValidationStatus conflicts={conflicts} isValidating={isValidating} />
              )}
            </main>

            <footer className={styles.dialogFooter}>
              <button type="button" onClick={closeDialog} className={styles.cancelButton}>
                Cancel
              </button>
              <button type="button" onClick={handleConfirm} className={styles.confirmButton}>
                OK
              </button>
            </footer>
          </dialog>
        </>
      )}
    </>
  );
};
